import { Injectable, OnDestroy } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { BehaviorSubject, combineLatest, from, Observable, of, Subscription } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators'
import { Scope } from '../models'
import { ApiService } from './api.service'

type State = {
  latestScopesListQueryTimestamp: number
  activeScopeId: string | null
}
const State = ({ latestScopesListQueryTimestamp, activeScopeId }: {latestScopesListQueryTimestamp: number, activeScopeId: string | null}): State =>
  ({ latestScopesListQueryTimestamp, activeScopeId })

type ServerQuery<Data> = LoadingQuery|ReadyQuery<Data>|FailedQuery

type LoadingQuery = {status: 'loading'}
type ReadyQuery<Data> = {status: 'ready', data:Data}
type FailedQuery = {status: 'failed'}

export const isQueryLoading = <Data>(query: ServerQuery<Data>): query is LoadingQuery => query.status === 'loading'
export const isQueryEnded = <Data>(query: ServerQuery<Data>): query is ReadyQuery<Data> | FailedQuery => !isQueryLoading(query)
export const isQueryReady = <Data>(query: ServerQuery<Data>): query is ReadyQuery<Data> => query.status === 'ready'
export const isQueryFailed = <Data>(query: ServerQuery<Data>): query is FailedQuery => query.status === 'failed'

const urlToRecapPathParts = (url:string):string[] => url.split('?')[0].split('/').splice(1)

@Injectable({ providedIn: 'root' })
export class ScopeService implements OnDestroy {

  private _state = new BehaviorSubject<{state: State, synchronizeUrl: boolean}>({
    state         : State({ latestScopesListQueryTimestamp: Date.now(), activeScopeId: null }),
    synchronizeUrl: false
  })
  state$ = this._state.pipe(
    switchMap(({ state, synchronizeUrl }) => {
      if(synchronizeUrl) {
        const [ previousScopeId, ...pathAfterScopeId ] = urlToRecapPathParts(this.router.url)
        if(state.activeScopeId === null) this.router.navigate([ '' ])
        return from(this.router.navigate([ state.activeScopeId, ...pathAfterScopeId ])).pipe(map(() => state))
      } else {
        return of(state)
      }
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  activeScopeId$ = this.state$.pipe(map(({ activeScopeId }) => activeScopeId), distinctUntilChanged(), shareReplay(1))

  private _scopesList: Scope.T[]
  scopesListQuery$: Observable<ServerQuery<Scope.T[]>> = this.state$.pipe(
    map(({ latestScopesListQueryTimestamp })=>latestScopesListQueryTimestamp),
    distinctUntilChanged(),
    switchMap(() =>this.api.getListScopes().pipe(
      map(data => ({
        status: 'ready' as const,
        data  : data.map(scope => Scope.from(scope))
      })),
      startWith({ status: 'loading' as const }),
      catchError(()=>of({ status: 'failed' as const }))
    )),
    shareReplay(1)
  )
  private _scope: Scope.T | null
  scope$ = combineLatest([ this.activeScopeId$, this.scopesListQuery$ ]).pipe(
    map(([ id, scopesQuery ]) => {
      if(!isQueryReady(scopesQuery)) return null
      return scopesQuery.data.find(scope => scope.id === id) ?? null
    }),
    shareReplay(1)
  )
  selectedScope$ = this.scope$.pipe(
    filter((scope): scope is Scope.T => scope !== null),
    distinctUntilChanged(),
    shareReplay(1)
  )
  scopeId$ = this.selectedScope$.pipe(
    map(scope => scope.id),
    distinctUntilChanged(),
    shareReplay(1)
  )
  private scopeIdFromRouterUrl$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(event => {
      const pathParts = urlToRecapPathParts((event as NavigationEnd).url)
      return (pathParts[0] === 'map') ? null : pathParts[0]
    }),
    distinctUntilChanged()
  )
  private subscriptions:Subscription[] = [
    this.scopesListQuery$.subscribe((scopesQuery) => this._scopesList = isQueryReady(scopesQuery) ? scopesQuery.data : []),
    this.scope$.subscribe(scope => this._scope = scope),
    this.scopeIdFromRouterUrl$.subscribe(scopeIdFromRouterUrl=> {
      if(this.state.activeScopeId !== scopeIdFromRouterUrl) {
        this.setState({ ...this.state, activeScopeId: scopeIdFromRouterUrl }, false)
      }
    }),

  ]
  constructor (
    private api: ApiService,
    private router: Router,
  ) {}

  ngOnDestroy (): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }
  private get state () : State {
    return this._state.value.state
  }

  private setState (nextState: State, synchronizeUrl = true) : void {
    return this._state.next({ state: nextState, synchronizeUrl })
  }

  refresh (): void {
    this.setState(State({ latestScopesListQueryTimestamp: Date.now(), activeScopeId: null }))
  }

  switchScope (scopeId: string): void {
    if (this._scope !== null && scopeId === Scope.idOf(this._scope)) return
    const nextScope = this._scopesList.find(scope => Scope.idOf(scope) === scopeId)
    if(nextScope === undefined){
      throw Error('scope not found')
    }
    if(!Scope.isViewAllowed(nextScope)){
      throw Error('this operation requires user to have View permission on selected scope')
    }
    this.setState(State({ ...this.state, activeScopeId: scopeId }))
  }
}
